import React, { useState, useEffect, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import validator from 'validator';
import { Button, Input, TextField, Typography, useTheme } from '@mui/material';
import { AppContext } from '@/AppContext';
import ErrorBlock from '@/components/ErrorBlock/ErrorBlock';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { trackButtonClick } from '@/lib/tracking';

const PREFIX = 'SignIn';

const classes = {
  root: `${PREFIX}-root`,
  grid: `${PREFIX}-grid`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  contentHeader: `${PREFIX}-contentHeader`,
  logoImage: `${PREFIX}-logoImage`,
  contentBody: `${PREFIX}-contentBody`,
  form: `${PREFIX}-form`,
  formContainer: `${PREFIX}-formContainer`,
  formInputsContainer: `${PREFIX}-formInputsContainer`,
  bottomText: `${PREFIX}-bottomText`,
  title: `${PREFIX}-title`,
  form__link: `${PREFIX}-form__link`,
  socialButtons: `${PREFIX}-socialButtons`,
  socialIcon: `${PREFIX}-socialIcon`,
  sugestion: `${PREFIX}-sugestion`,
  textField: `${PREFIX}-textField`,
  signInButton: `${PREFIX}-signInButton`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    height: '100%',
  },

  [`& .${classes.grid}`]: {
    height: '100%',
  },

  [`& .${classes.contentContainer}`]: {
    backgroundColor: 'white',
  },

  [`& .${classes.content}`]: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.contentHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBototm: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  [`& .${classes.logoImage}`]: {
    marginLeft: theme.spacing(4),
  },

  [`& .${classes.contentBody}`]: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.form}`]: {
    backgroundColor: 'white',
    height: '450px',
    width: '640px',
    padding: '30px',
    borderRadius: '8px',
  },

  [`& .${classes.formContainer}`]: {
    height: '100%',
    padding: '20px  45px',
    border: '1px solid #ffffff',
    borderBottomColor: '#DEDEDE',
    borderTopColor: '#DEDEDE',
  },

  [`& .${classes.formInputsContainer}`]: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },

  [`& .${classes.bottomText}`]: {
    marginTop: '8px',
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#213955',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '16px',
  },

  [`& .${classes.title}`]: {
    color: '#213955',
    textAlign: 'center',
    fontFamily: 'Roboto',
    fontSize: '22px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '28px',
    letterSpacing: '-0.06px',
  },

  [`& .${classes.form__link}`]: {
    ['@media (max-width:768px)']: {
      textAlign: 'center',
      fontSize: 20,
      margin: 25,
    },
  },

  [`& .${classes.socialButtons}`]: {
    marginTop: theme.spacing(3),
  },

  [`& .${classes.socialIcon}`]: {
    marginRight: theme.spacing(1),
  },

  [`& .${classes.sugestion}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.textField}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.signInButton}`]: {
    margin: theme.spacing(2, 0),
  },
}));

const SignIn = () => {
  const theme = useTheme();

  const [inputType, setInputType] = useState('password');
  const [formState, setFormState] = useState({
    isValid: true,
    values: {},
    touched: {},
    errors: {},
  });

  useEffect(() => {
    const validateForm = values => {
      const errors = {};

      if (!values.email || validator.isEmpty(values.email)) {
        errors.email = ['Email is required'];
      } else if (!validator.isEmail(values.email)) {
        errors.email = ['Please enter a valid email address'];
      } else if (!validator.isLength(values.email, { max: 64 })) {
        errors.email = ['Email address must be less than 64 characters'];
      }

      if (!values.password || validator.isEmpty(values.password)) {
        errors.password = ['Password is required'];
      } else if (!validator.isLength(values.password, { max: 128 })) {
        errors.password = ['Password must be less than 128 characters'];
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };

    const errors = validateForm(formState.values);

    setFormState(formState => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors || {},
    }));
  }, [formState.values]);

  const handleChange = event => {
    event.persist();

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const appContext = useContext(AppContext);

  const handleSignIn = event => {
    event.preventDefault();
    trackButtonClick({ button: 'login', location: 'sign_in' });
    appContext.authApi
      .login({
        login: formState.values.email,
        password: formState.values.password,
      })
      .then(payload => {
        appContext.login(payload.data);
        appContext.history.push('/');
      })
      .catch(error => {
        const data = error.response.data;
        setFormState(formState => ({
          ...formState,
          errors: {
            email: [data.message],
            password: [data.message],
          },
        }));
      });
  };

  const showPassword = () => {
    if (inputType === 'password') {
      setInputType('text');
    } else {
      setInputType('password');
    }
  };
  const Redirect = event => {
    // event.preventDefault()
    appContext.history.push('/request-change-password');
  };
  const hasError = field => (formState.touched[field] && formState.errors[field] ? true : false);

  return (
    <Root className={classes.root}>
      <div className={classes.content}>
        <div className={classes.contentBody}>
          <ErrorBlock message={formState.errors.token} />
          <div className={classes.form}>
            <div style={{ height: '100%' }}>
              <div style={{ height: '28px', marginBottom: '24px', cursor: 'pointer' }}>
                <div className={classes.title}>
                  <div className={'text'} id={'sing-in'}>
                    Вход администратора
                  </div>
                </div>
              </div>
              <div style={{ height: 'calc(100% - 145px)' }}>
                <div className={classes.formContainer}>
                  <form className={classes.formInputsContainer} onSubmit={handleSignIn}>
                    <div style={{ width: '100%', height: '100%' }}>
                      <TextField
                        className={classes.textField}
                        error={hasError('email')}
                        fullWidth
                        helperText={hasError('email') ? formState.errors.email[0] : null}
                        label='Адрес электронной почты'
                        name='email'
                        onChange={handleChange}
                        type='text'
                        value={formState.values.email || ''}
                        variant='outlined'
                      />
                      <TextField
                        className={classes.textField}
                        error={hasError('password')}
                        fullWidth
                        helperText={hasError('password') ? formState.errors.password[0] : null}
                        label='Пароль'
                        name='password'
                        onChange={handleChange}
                        type={inputType}
                        value={formState.values.password || ''}
                        variant='outlined'
                      />
                      <span style={{ position: 'relative' }}>
                        {inputType === 'password' ? (
                          <VisibilityOff
                            onClick={showPassword}
                            style={{ position: 'absolute', top: 32, right: 10, cursor: 'pointer' }}
                          />
                        ) : (
                          <Visibility
                            onClick={showPassword}
                            style={{ position: 'absolute', top: 32, right: 10, cursor: 'pointer' }}
                          />
                        )}
                      </span>
                    </div>
                    <div style={{ width: '100%', height: '100%', marginTop: '4px' }}>
                      <Button
                        className={classes.signInButton}
                        color='primary'
                        fullWidth
                        size='large'
                        type='submit'
                        variant='contained'
                        data-test-id='sign-in-button'
                      >
                        Войти
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
              <div className={classes.bottomText} style={{ marginTop: '16px' }}>
                <p
                  onClick={() => appContext.history.push('/registration')}
                  style={{ cursor: 'pointer' }}
                  data-test-id='registration'
                >
                  Регистрация аккаунта
                </p>
              </div>
              <div className={classes.bottomText}>
                <p
                  onClick={() => appContext.history.push('/request-change-password')}
                  style={{ cursor: 'pointer' }}
                  data-test-id='restore'
                >
                  Восстановить пароль
                </p>
              </div>
              <div className={classes.bottomText}>
                <p
                  onClick={() => appContext.history.push('/sign-in-reception')}
                  style={{ cursor: 'pointer', fontWeight: 'bold' }}
                  data-test-id='reception-login'
                >
                  Вход регистратора
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Root>
  );
};

SignIn.propTypes = {
  history: PropTypes.object,
};

export default SignIn;
